<template>
  <section class="daycare-insights">
    <div class="insights-heading">
      <span class="heading-mark"><v-icon>mdi-chart-box-outline</v-icon></span>
      <div>
        <h3>Kinderhort-Statistik</h3>
        <p>Nachfrage, Buchungsverhalten und Spitzenzeiten im gewählten Zeitraum.</p>
      </div>
    </div>

    <v-card dark flat class="filter-card">
      <div class="filter-copy">
        <span>Auswertungszeitraum</span>
        <strong>{{ formattedDateRange }}</strong>
      </div>

      <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
        <template v-slot:activator="{ on, attrs }">
          <v-btn outlined color="#58d388" class="date-action" v-bind="attrs" v-on="on">
            <v-icon left>mdi-calendar-range</v-icon>
            Zeitraum ändern
          </v-btn>
        </template>
        <v-date-picker v-model="draftDateRange" :max="today" range scrollable locale="de-CH">
          <v-spacer></v-spacer>
          <v-btn text color="grey lighten-1" @click="cancelDateRange">Abbrechen</v-btn>
          <v-btn text color="#58d388" :disabled="!isDraftRangeReady" @click="applyDateRange">Anwenden</v-btn>
        </v-date-picker>
      </v-menu>
    </v-card>

    <div v-if="isLoading" class="loading-state">
      <v-progress-circular indeterminate color="#58d388" size="48"></v-progress-circular>
      <p>Statistik wird geladen ...</p>
    </div>

    <v-alert v-else-if="loadError" outlined type="error" class="status-alert">
      {{ loadError }}
      <v-btn text color="white" class="retry-action" @click="fetchStatistics">Erneut versuchen</v-btn>
    </v-alert>

    <template v-else>
      <div class="metric-grid">
        <article v-for="metric in metricCards" :key="metric.label" class="metric-card">
          <span class="metric-icon" :class="metric.tone"><v-icon>{{ metric.icon }}</v-icon></span>
          <div>
            <span class="metric-label">{{ metric.label }}</span>
            <strong class="metric-value">{{ metric.value }}</strong>
            <small>{{ metric.detail }}</small>
          </div>
        </article>
      </div>

      <template v-if="activeBookingCount > 0">
        <div class="chart-grid">
          <v-card dark flat class="chart-card">
            <div class="chart-card-header">
              <span class="chart-mark"><v-icon>mdi-calendar-week</v-icon></span>
              <div>
                <h4>Nachfrage nach Wochentag</h4>
                <p>Durchschnittlich gebuchte Plätze pro Kalendertag</p>
              </div>
            </div>
            <apexchart type="bar" height="320" :options="weekdayChartOptions" :series="weekdayChartSeries"></apexchart>
          </v-card>

          <v-card dark flat class="chart-card">
            <div class="chart-card-header">
              <span class="chart-mark chart-mark--blue"><v-icon>mdi-chart-timeline-variant</v-icon></span>
              <div>
                <h4>Entwicklung pro Woche</h4>
                <p>Gebuchte Kinder- und Babyplätze im zeitlichen Verlauf</p>
              </div>
            </div>
            <apexchart type="area" height="320" :options="weeklyChartOptions" :series="weeklyChartSeries"></apexchart>
          </v-card>
        </div>

        <div class="insight-grid">
          <article v-for="insight in insightCards" :key="insight.label" class="insight-card">
            <span class="insight-icon"><v-icon>{{ insight.icon }}</v-icon></span>
            <div>
              <span>{{ insight.label }}</span>
              <strong>{{ insight.value }}</strong>
              <small>{{ insight.detail }}</small>
            </div>
          </article>
        </div>
      </template>

      <div v-else class="empty-state">
        <v-icon>mdi-chart-box-outline</v-icon>
        <h4>Keine aktiven Buchungen</h4>
        <p>Für den gewählten Zeitraum sind noch keine aktiven Kinderhort-Buchungen vorhanden.</p>
      </div>

      <aside class="capacity-note">
        <span><v-icon>mdi-information-outline</v-icon></span>
        <div>
          <strong>Belegung statt Auslastung</strong>
          <p>
            Die Auswertung zeigt gebuchte Plätze. Eine Auslastung in Prozent ist erst möglich, sobald eine maximale Kapazität pro
            Betreuungstag oder Zeitfenster hinterlegt wird.
          </p>
        </div>
      </aside>
    </template>
  </section>
</template>

<script>
import api from "../../../api"
import moment from "moment"

const weekdays = [
  { key: 1, label: "Montag", shortLabel: "Mo" },
  { key: 2, label: "Dienstag", shortLabel: "Di" },
  { key: 3, label: "Mittwoch", shortLabel: "Mi" },
  { key: 4, label: "Donnerstag", shortLabel: "Do" },
  { key: 5, label: "Freitag", shortLabel: "Fr" },
  { key: 6, label: "Samstag", shortLabel: "Sa" },
  { key: 7, label: "Sonntag", shortLabel: "So" },
]

export default {
  name: "StudioDashboardDaycareInsights",
  data() {
    const today = moment()
    const defaultRange = [today.clone().subtract(11, "weeks").startOf("isoWeek").format("YYYY-MM-DD"), today.format("YYYY-MM-DD")]

    return {
      dateMenu: false,
      dateRange: defaultRange,
      draftDateRange: defaultRange.slice(),
      statistic: [],
      isLoading: false,
      loadError: "",
      today: today.format("YYYY-MM-DD"),
    }
  },
  computed: {
    isDraftRangeReady() {
      return Array.isArray(this.draftDateRange) && this.draftDateRange.filter(Boolean).length === 2
    },
    formattedDateRange() {
      if (!Array.isArray(this.dateRange) || this.dateRange.length !== 2) return "Kein Zeitraum gewählt"
      return `${moment(this.dateRange[0]).format("DD.MM.YYYY")} – ${moment(this.dateRange[1]).format("DD.MM.YYYY")}`
    },
    normalizedEntries() {
      return (Array.isArray(this.statistic) ? this.statistic : [])
        .filter((entry) => entry && entry.date && moment(entry.date, "YYYY-MM-DD", true).isValid())
        .map((entry) => ({
          ...entry,
          signups: Array.isArray(entry.signups) ? entry.signups : [],
        }))
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
    },
    allSignups() {
      return this.normalizedEntries.reduce((rows, entry) => {
        const entryRows = entry.signups.map((signup) => ({
          date: entry.date,
          signup,
        }))
        return rows.concat(entryRows)
      }, [])
    },
    activeSignups() {
      return this.allSignups.filter((row) => !this.isCancelled(row.signup))
    },
    activeBookingCount() {
      return this.activeSignups.length
    },
    uniqueFamilyCount() {
      const userIds = new Set()
      this.activeSignups.forEach((row) => {
        const userId = this.getUserId(row.signup)
        if (userId) userIds.add(userId)
      })
      return userIds.size
    },
    totalChildren() {
      return this.activeSignups.reduce((total, row) => total + this.getAmount(row.signup.amountChildren), 0)
    },
    totalBabies() {
      return this.activeSignups.reduce((total, row) => total + this.getAmount(row.signup.amountBabies), 0)
    },
    totalPlaces() {
      return this.totalChildren + this.totalBabies
    },
    cancelledCount() {
      return this.allSignups.filter((row) => this.isCancelled(row.signup)).length
    },
    averagePlacesPerBooking() {
      return this.activeBookingCount ? this.totalPlaces / this.activeBookingCount : 0
    },
    babyShare() {
      return this.totalPlaces ? (this.totalBabies / this.totalPlaces) * 100 : 0
    },
    metricCards() {
      return [
        {
          label: "Aktive Buchungen",
          value: this.formatInteger(this.activeBookingCount),
          detail: `${this.formatInteger(this.uniqueFamilyCount)} ${this.uniqueFamilyCount === 1 ? "Familie" : "Familien"}`,
          icon: "mdi-calendar-check-outline",
          tone: "metric-icon--green",
        },
        {
          label: "Gebuchte Kinderplätze",
          value: this.formatInteger(this.totalChildren),
          detail: "Summe im Zeitraum",
          icon: "mdi-human-child",
          tone: "metric-icon--blue",
        },
        {
          label: "Gebuchte Babyplätze",
          value: this.formatInteger(this.totalBabies),
          detail: "Summe im Zeitraum",
          icon: "mdi-baby-carriage",
          tone: "metric-icon--violet",
        },
        {
          label: "Abmeldungen",
          value: this.formatInteger(this.cancelledCount),
          detail: "Aktueller Buchungsstatus",
          icon: "mdi-calendar-remove-outline",
          tone: "metric-icon--red",
        },
      ]
    },
    weekdayStats() {
      const stats = weekdays.map((weekday) => ({
        ...weekday,
        occurrences: 0,
        children: 0,
        babies: 0,
      }))

      const cursor = moment(this.dateRange[0]).startOf("day")
      const end = moment(this.dateRange[1]).startOf("day")
      while (cursor.isSameOrBefore(end, "day")) {
        stats[cursor.isoWeekday() - 1].occurrences += 1
        cursor.add(1, "day")
      }

      this.normalizedEntries.forEach((entry) => {
        const weekdayIndex = moment(entry.date).isoWeekday() - 1
        const totals = this.getEntryTotals(entry)
        stats[weekdayIndex].children += totals.children
        stats[weekdayIndex].babies += totals.babies
      })

      return stats.map((stat) => ({
        ...stat,
        averageChildren: stat.occurrences ? this.roundToOne(stat.children / stat.occurrences) : 0,
        averageBabies: stat.occurrences ? this.roundToOne(stat.babies / stat.occurrences) : 0,
        averagePlaces: stat.occurrences ? this.roundToOne((stat.children + stat.babies) / stat.occurrences) : 0,
      }))
    },
    weekdayChartSeries() {
      return [
        {
          name: "Kinder",
          data: this.weekdayStats.map((stat) => stat.averageChildren),
        },
        {
          name: "Babies",
          data: this.weekdayStats.map((stat) => stat.averageBabies),
        },
      ]
    },
    weekdayChartOptions() {
      return {
        chart: {
          background: "transparent",
          fontFamily: "inherit",
          foreColor: "#a1a1aa",
          stacked: true,
          toolbar: { show: false },
        },
        colors: ["#58d388", "#60a5fa"],
        dataLabels: { enabled: false },
        grid: {
          borderColor: "rgba(255,255,255,0.08)",
          strokeDashArray: 4,
        },
        legend: {
          fontSize: "0.75rem",
          position: "top",
          horizontalAlign: "right",
          labels: { colors: "#d4d4d8" },
        },
        plotOptions: {
          bar: {
            borderRadius: 5,
            columnWidth: "58%",
          },
        },
        tooltip: {
          theme: "dark",
          y: {
            formatter: (value) => `${this.formatDecimal(value)} Plätze`,
          },
        },
        xaxis: {
          categories: this.weekdayStats.map((stat) => stat.shortLabel),
          axisBorder: { color: "rgba(255,255,255,0.08)" },
          axisTicks: { color: "rgba(255,255,255,0.08)" },
          labels: {
            style: {
              colors: "#a1a1aa",
              fontSize: "0.75rem",
            },
          },
        },
        yaxis: {
          min: 0,
          labels: {
            formatter: (value) => this.formatChartValue(value),
            style: {
              colors: "#a1a1aa",
              fontSize: "0.75rem",
            },
          },
        },
      }
    },
    weeklyStats() {
      const weeks = []
      const weekByKey = {}
      const cursor = moment(this.dateRange[0]).startOf("isoWeek")
      const end = moment(this.dateRange[1]).startOf("isoWeek")

      while (cursor.isSameOrBefore(end, "week")) {
        const key = cursor.format("GGGG-[W]WW")
        const week = {
          key,
          label: cursor.format("DD.MM.YY"),
          children: 0,
          babies: 0,
        }
        weeks.push(week)
        weekByKey[key] = week
        cursor.add(1, "week")
      }

      this.normalizedEntries.forEach((entry) => {
        const key = moment(entry.date).format("GGGG-[W]WW")
        const week = weekByKey[key]
        if (!week) return
        const totals = this.getEntryTotals(entry)
        week.children += totals.children
        week.babies += totals.babies
      })

      return weeks
    },
    weeklyChartSeries() {
      return [
        {
          name: "Kinder",
          data: this.weeklyStats.map((week) => week.children),
        },
        {
          name: "Babies",
          data: this.weeklyStats.map((week) => week.babies),
        },
      ]
    },
    weeklyChartOptions() {
      return {
        chart: {
          background: "transparent",
          fontFamily: "inherit",
          foreColor: "#a1a1aa",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#58d388", "#60a5fa"],
        dataLabels: { enabled: false },
        fill: {
          type: "gradient",
          gradient: {
            opacityFrom: 0.34,
            opacityTo: 0.04,
          },
        },
        grid: {
          borderColor: "rgba(255,255,255,0.08)",
          strokeDashArray: 4,
        },
        legend: {
          fontSize: "0.75rem",
          position: "top",
          horizontalAlign: "right",
          labels: { colors: "#d4d4d8" },
        },
        markers: {
          size: 3,
          strokeWidth: 0,
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        tooltip: {
          theme: "dark",
          y: {
            formatter: (value) => `${this.formatInteger(value)} Plätze`,
          },
        },
        xaxis: {
          categories: this.weeklyStats.map((week) => week.label),
          axisBorder: { color: "rgba(255,255,255,0.08)" },
          axisTicks: { color: "rgba(255,255,255,0.08)" },
          labels: {
            hideOverlappingLabels: true,
            rotate: -45,
            style: {
              colors: "#a1a1aa",
              fontSize: "0.75rem",
            },
          },
        },
        yaxis: {
          min: 0,
          labels: {
            formatter: (value) => this.formatChartValue(value),
            style: {
              colors: "#a1a1aa",
              fontSize: "0.75rem",
            },
          },
        },
      }
    },
    strongestWeekday() {
      return this.weekdayStats.reduce((strongest, stat) => {
        if (!strongest || stat.averagePlaces > strongest.averagePlaces) return stat
        return strongest
      }, null)
    },
    peakDay() {
      return this.normalizedEntries.reduce((peak, entry) => {
        const totals = this.getEntryTotals(entry)
        const totalPlaces = totals.children + totals.babies
        if (!peak || totalPlaces > peak.totalPlaces) {
          return {
            date: entry.date,
            totalPlaces,
          }
        }
        return peak
      }, null)
    },
    popularArrivalWindow() {
      const windows = {}
      this.activeSignups.forEach((row) => {
        const hour = this.getArrivalHour(row.signup.arrivalTime)
        if (hour === null) return
        if (!windows[hour]) windows[hour] = 0
        windows[hour] += 1
      })

      const bestHour = Object.keys(windows).reduce((best, hour) => {
        if (best === null || windows[hour] > windows[best]) return hour
        return best
      }, null)

      if (bestHour === null) {
        return {
          label: "Keine Zeiten",
          count: 0,
        }
      }

      const hour = Number(bestHour)
      const nextHour = (hour + 1) % 24
      return {
        label: `${this.padHour(hour)}:00 – ${this.padHour(nextHour)}:00`,
        count: windows[bestHour],
      }
    },
    insightCards() {
      const strongest = this.strongestWeekday
      const peak = this.peakDay
      const arrival = this.popularArrivalWindow

      return [
        {
          label: "Stärkster Wochentag",
          value: strongest && strongest.averagePlaces > 0 ? strongest.label : "Keine Daten",
          detail: strongest && strongest.averagePlaces > 0 ? `Ø ${this.formatDecimal(strongest.averagePlaces)} Plätze` : "Noch keine Nachfrage",
          icon: "mdi-calendar-star",
        },
        {
          label: "Spitzentag",
          value: peak && peak.totalPlaces > 0 ? moment(peak.date).locale("de-ch").format("dd, DD.MM.YYYY") : "Keine Daten",
          detail: peak && peak.totalPlaces > 0 ? `${this.formatInteger(peak.totalPlaces)} Plätze` : "Noch keine Nachfrage",
          icon: "mdi-trending-up",
        },
        {
          label: "Beliebteste Ankunftszeit",
          value: arrival.label,
          detail: arrival.count
            ? `${this.formatInteger(arrival.count)} ${arrival.count === 1 ? "Buchung" : "Buchungen"} mit Zeitangabe`
            : "Keine Ankunftszeiten erfasst",
          icon: "mdi-clock-outline",
        },
        {
          label: "Durchschnitt pro Buchung",
          value: `${this.formatDecimal(this.averagePlacesPerBooking)} Plätze`,
          detail: `${this.formatDecimal(this.babyShare)} % Baby-Anteil`,
          icon: "mdi-account-group-outline",
        },
      ]
    },
  },
  watch: {
    dateMenu(isOpen) {
      if (isOpen) this.draftDateRange = this.dateRange.slice()
    },
  },
  created() {
    this.fetchStatistics()
  },
  methods: {
    cancelDateRange() {
      this.draftDateRange = this.dateRange.slice()
      this.dateMenu = false
    },
    applyDateRange() {
      if (!this.isDraftRangeReady) return
      this.dateRange = this.draftDateRange.filter(Boolean).sort()
      this.draftDateRange = this.dateRange.slice()
      this.dateMenu = false
      this.fetchStatistics()
    },
    async fetchStatistics() {
      if (!Array.isArray(this.dateRange) || this.dateRange.length !== 2) return
      this.isLoading = true
      this.loadError = ""

      try {
        const result = await api.fetchDaycareStatistic(this.dateRange[0], this.dateRange[1])
        this.statistic = Array.isArray(result) ? result : []
      } catch (error) {
        console.error("Fehler beim Laden der Kinderhort-Statistik:", error)
        this.statistic = []
        this.loadError = "Die Kinderhort-Statistik konnte nicht geladen werden."
      } finally {
        this.isLoading = false
      }
    },
    isCancelled(signup) {
      if (!signup) return true
      return signup.active === false || this.getAmount(signup.amountChildren) + this.getAmount(signup.amountBabies) === 0
    },
    getAmount(value) {
      const amount = Number(value)
      return Number.isFinite(amount) && amount > 0 ? amount : 0
    },
    getUserId(signup) {
      if (!signup || !signup.user) return null
      const userId = signup.user._id || signup.user.id
      return userId ? String(userId) : null
    },
    getEntryTotals(entry) {
      return (entry.signups || []).reduce(
        (totals, signup) => {
          if (this.isCancelled(signup)) return totals
          totals.children += this.getAmount(signup.amountChildren)
          totals.babies += this.getAmount(signup.amountBabies)
          return totals
        },
        { children: 0, babies: 0 }
      )
    },
    getArrivalHour(value) {
      if (!value) return null
      const match = String(value)
        .trim()
        .match(/^(\d{1,2})(?::?\d{2})?/)
      if (!match) return null
      const hour = Number(match[1])
      return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : null
    },
    padHour(hour) {
      return String(hour).padStart(2, "0")
    },
    roundToOne(value) {
      return Math.round((Number(value) || 0) * 10) / 10
    },
    formatInteger(value) {
      return Math.round(Number(value) || 0).toLocaleString("de-CH")
    },
    formatDecimal(value) {
      return (Number(value) || 0).toLocaleString("de-CH", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    },
    formatChartValue(value) {
      return (Number(value) || 0).toLocaleString("de-CH", {
        maximumFractionDigits: 1,
      })
    },
  },
}
</script>

<style scoped>
.daycare-insights {
  max-width: 80rem;
  margin: 0 auto;
  color: #f4f4f5;
  font-size: 1rem;
}

.insights-heading,
.chart-card-header,
.filter-card,
.metric-card,
.insight-card,
.capacity-note {
  display: flex;
  align-items: center;
}

.insights-heading {
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.heading-mark,
.chart-mark,
.metric-icon,
.insight-icon,
.capacity-note > span {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.heading-mark {
  width: 2.75rem;
  height: 2.75rem;
  border: 1px solid rgba(88, 211, 136, 0.24);
  border-radius: 0.8125rem;
  background: rgba(88, 211, 136, 0.11);
}

.heading-mark .v-icon,
.chart-mark .v-icon {
  color: #58d388;
}

.insights-heading h3,
.chart-card-header h4,
.empty-state h4 {
  margin: 0;
  color: #f4f4f5;
}

.insights-heading h3 {
  font-size: 1.125rem;
}

.insights-heading p,
.chart-card-header p,
.empty-state p,
.capacity-note p {
  margin: 0.25rem 0 0;
  color: #a1a1aa;
  font-size: 0.875rem;
  line-height: 1.5;
}

.filter-card {
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 1rem !important;
  background: rgba(255, 255, 255, 0.025) !important;
}

.filter-copy {
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
}

.filter-copy span,
.metric-label,
.insight-card span {
  color: #a1a1aa;
  font-size: 0.75rem;
  font-weight: 750;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.filter-copy strong {
  color: #f4f4f5;
  font-size: 1rem;
}

.date-action,
.retry-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.loading-state,
.empty-state {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  min-height: 18rem;
  padding: 2rem;
  text-align: center;
}

.loading-state p {
  margin: 1rem 0 0;
  color: #d4d4d8;
  font-size: 1rem;
}

.status-alert {
  margin-top: 1rem;
  font-size: 1rem;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  min-width: 0;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.028);
}

.metric-card > div,
.insight-card > div,
.chart-card-header > div {
  min-width: 0;
}

.metric-icon {
  width: 2.75rem;
  height: 2.75rem;
  border: 1px solid transparent;
  border-radius: 0.8125rem;
}

.metric-icon--green {
  border-color: rgba(88, 211, 136, 0.22);
  background: rgba(88, 211, 136, 0.11);
}

.metric-icon--green .v-icon {
  color: #58d388;
}

.metric-icon--blue {
  border-color: rgba(96, 165, 250, 0.22);
  background: rgba(96, 165, 250, 0.11);
}

.metric-icon--blue .v-icon {
  color: #60a5fa;
}

.metric-icon--violet {
  border-color: rgba(167, 139, 250, 0.22);
  background: rgba(167, 139, 250, 0.11);
}

.metric-icon--violet .v-icon {
  color: #a78bfa;
}

.metric-icon--red {
  border-color: rgba(248, 113, 113, 0.22);
  background: rgba(248, 113, 113, 0.1);
}

.metric-icon--red .v-icon {
  color: #f87171;
}

.metric-value {
  display: block;
  margin-top: 0.125rem;
  color: #f4f4f5;
  font-size: 1.75rem;
  line-height: 1.1;
}

.metric-card small,
.insight-card small {
  display: block;
  margin-top: 0.25rem;
  color: #a1a1aa;
  font-size: 0.75rem;
  line-height: 1.4;
}

.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.chart-card {
  min-width: 0;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 1.125rem !important;
  background: rgba(20, 20, 21, 0.76) !important;
}

.chart-card-header {
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.chart-mark {
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgba(88, 211, 136, 0.22);
  border-radius: 0.75rem;
  background: rgba(88, 211, 136, 0.1);
}

.chart-mark--blue {
  border-color: rgba(96, 165, 250, 0.22);
  background: rgba(96, 165, 250, 0.1);
}

.chart-mark--blue .v-icon {
  color: #60a5fa;
}

.chart-card-header h4 {
  font-size: 1rem;
}

.insight-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.insight-card {
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.022);
}

.insight-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.1);
}

.insight-icon .v-icon {
  color: #fbbf24;
}

.insight-card strong {
  display: block;
  margin-top: 0.1875rem;
  overflow-wrap: anywhere;
  color: #f4f4f5;
  font-size: 1rem;
  line-height: 1.35;
}

.empty-state {
  margin-top: 1rem;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.018);
}

.empty-state > .v-icon {
  margin-bottom: 0.75rem;
  color: #71717a;
  font-size: 2.5rem;
}

.empty-state h4 {
  font-size: 1rem;
}

.capacity-note {
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(96, 165, 250, 0.18);
  border-radius: 1rem;
  background: rgba(96, 165, 250, 0.06);
}

.capacity-note > span {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: rgba(96, 165, 250, 0.1);
}

.capacity-note .v-icon {
  color: #60a5fa;
}

.capacity-note strong {
  color: #dbeafe;
  font-size: 1rem;
}

@media (max-width: 74.9375rem) {
  .metric-grid,
  .insight-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .chart-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 37.4375rem) {
  .filter-card {
    align-items: stretch;
    flex-direction: column;
    padding: 0.875rem;
  }

  .date-action {
    width: 100%;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .metric-card {
    align-items: flex-start;
    flex-direction: column;
    padding: 0.875rem;
  }

  .metric-value {
    font-size: 1.5rem;
  }

  .chart-card {
    padding: 0.875rem;
  }

  .insight-grid {
    grid-template-columns: 1fr;
  }

  .capacity-note {
    padding: 0.875rem;
  }
}
</style>
